import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {BokkzClient, IntermediaryEntity} from '../services/api.bokkz.services';
import {BokkzWageDataService} from '../services/bokkz-wage-data-service';
import {BokkzAlgDataService} from '../services/bokkz-alg-data-service';
import {DetailsConfig, SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {Subject, takeUntil} from 'rxjs';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiTabModule} from '@snelstart/ui-tab';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-domain-logic-template',
  styleUrls: ['./template.component.css'],
  standalone: true,
  templateUrl: './template.component.html',
  imports: [
    SharedUiButtonModule,
    SharedUiDialogModule,
    SharedUiTabModule,
    SharedUiAlertModule,
    ReactiveFormsModule,
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class TemplateComponent implements OnInit, OnDestroy {

  @Input() public dialogVisible = false;
  @Input() public sstRelationId: any;
  @Output() public dialogVisibleChange = new EventEmitter<boolean>();

  private destroy$ = new Subject<void>();
  public intermediaryEntity = {} as IntermediaryEntity;
  public officeTemplateFormGroup = {} as FormGroup;

  private readonly MAX_IMAGE_BYTES = 2 * 1024 * 1024;
  private readonly MAX_IMAGE_WIDTH = 4000;
  private readonly MAX_IMAGE_HEIGHT = 4000;
  private readonly MAX_TOTAL_PIXELS = 8_000_000;
  private readonly PDF_A4_WIDTH = 595.44;
  private readonly PDF_A4_HEIGHT = 842.4;
  private readonly PREVIEW_A4_WIDTH = 160;
  private readonly PREVIEW_A4_HEIGHT = 226;
  private readonly IMAGE_DPI = 150;
  private readonly PDF_POINTS_PER_INCH = 72;
  private readonly HEADER_MAX_HEIGHT = 90;
  private readonly HEADER_MAX_WIDTH = 180;
  private readonly FOOTER_HORIZONTAL_MARGIN = 50;
  private readonly FOOTER_MAX_HEIGHT = 80;
  public headerPreviewStyle: Record<string, string> | null = null;
  public footerPreviewStyle: Record<string, string> | null = null;
  public footerTextStyle: Record<string, string> | null = null;
  public headerPreviewUrl: string | null = null;
  public footerPreviewUrl: string | null = null;

  public footerTypeOptions = [
    { label: 'Afbeelding', value: 'image' },
    { label: 'Tekst', value: 'text' },
  ];
  public logoPositionOptions = [
    { label: 'Links', value: 'left' },
    { label: 'Rechts', value: 'right' },
  ];

  public detailsConfig = DetailsConfig.create({
    hideArrowButtons: true,
  });

  constructor(
    public fb: FormBuilder,
    public bokkzClient: BokkzClient,
    public algDataService: BokkzAlgDataService,
    public dataService: BokkzWageDataService,
  ) {
    this.officeTemplateFormGroup = this.fb.group({
      marginTop: new FormControl(['25', [Validators.min(0), Validators.max(80)]]),
      marginRight: ['30', [Validators.min(0), Validators.max(80)]],
      marginBottom: ['25', [Validators.min(0), Validators.max(80)]],
      marginLeft: ['30', [Validators.min(0), Validators.max(80)]],
      headerLogoPosition: this.fb.control<'left' | 'right'>('left'),
      headerLogo: this.fb.control<File | null>(null),
      footerLogo: this.fb.control<File | null>(null),
      footerType: this.fb.control<'image' | 'text'>('image'),
      footerText: [null as unknown as string, [Validators.maxLength(200)]],
    })
  }

  ngOnInit(): void {
    this.algDataService.intermediary.pipe(takeUntil(this.destroy$)).subscribe((intermediaryEntity) => {
      this.intermediaryEntity = intermediaryEntity;
    });
    if (this.officeTemplateFormGroup !== undefined) {
      this.officeTemplateFormGroup.valueChanges.subscribe(async () => {
        const headerFile = this.officeTemplateFormGroup.get('headerLogo')?.value;
        const footerFile = this.officeTemplateFormGroup.get('footerLogo')?.value;
        if (headerFile) {
          await this.buildHeaderPreviewStyle(headerFile);
        }
        if (footerFile) {
          await this.buildFooterPreviewStyle(footerFile);
        }
      });
    }
  }

  ngOnDestroy() : void {
    if (this.headerPreviewUrl) {
      URL.revokeObjectURL(this.headerPreviewUrl);
    }
    if (this.footerPreviewUrl) {
      URL.revokeObjectURL(this.footerPreviewUrl);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  public saveTemplate(): void {
    // this.bokkzClient.postTemplate(this.officeTemplateFormGroup.getRawValue())
    //   .subscribe({
    //     next: () => {
    //       // success handling
    //     },
    //     error: () => {
    //       // error handling
    //     }
    //   });
  }

  private pixelsToPoints(pixels: number): number {
    return (pixels / this.IMAGE_DPI) * this.PDF_POINTS_PER_INCH;
  }

  private scaleToFitBox(naturalWidth: number, naturalHeight: number, maxWidth: number, maxHeight: number): { width: number; height: number } {
    if (!naturalWidth || !naturalHeight) {
      return { width: 0, height: 0 };
    }
    const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight, 1);
    return {
      width: naturalWidth * scale,
      height: naturalHeight * scale,
    };
  }

  public toPreviewX(valueInPdfPoints: number): number {
    return (valueInPdfPoints / this.PDF_A4_WIDTH) * this.PREVIEW_A4_WIDTH;
  }

  public toPreviewY(valueInPdfPoints: number): number {
    return (valueInPdfPoints / this.PDF_A4_HEIGHT) * this.PREVIEW_A4_HEIGHT;
  }

  private async buildHeaderPreviewStyle(file: File): Promise<void> {
    const size = await this.getImageSize(file);

    const naturalWidth = this.pixelsToPoints(size.width);
    const naturalHeight = this.pixelsToPoints(size.height);

    const headerSize = this.scaleToFitBox(
      naturalWidth,
      naturalHeight,
      this.HEADER_MAX_WIDTH,
      this.HEADER_MAX_HEIGHT
    );

    const topMargin = +(this.officeTemplateFormGroup.get('marginTop')?.value || 25);
    const leftMargin = +(this.officeTemplateFormGroup.get('marginLeft')?.value || 30);
    const rightMargin = +(this.officeTemplateFormGroup.get('marginRight')?.value || 30);
    const position = this.officeTemplateFormGroup.get('headerLogoPosition')?.value || 'left';

    const style: Record<string, string> = {
      top: `${this.toPreviewY(topMargin)}px`,
      width: `${this.toPreviewX(headerSize.width)}px`,
      height: `${this.toPreviewY(headerSize.height)}px`,
    };

    if (position === 'right') {
      const visualCorrection = 0;
      style['right'] = `${this.toPreviewX(rightMargin) - visualCorrection}px`;
    } else {
      style['left'] = `${this.toPreviewX(leftMargin)}px`;
    }

    this.headerPreviewStyle = style;
  }

  private async buildFooterPreviewStyle(file: File): Promise<void> {
    const size = await this.getImageSize(file);

    const footerPlacement = this.scaleToFitPageWidthWithOptionalMargin(
      size.width,
      size.height,
      this.PDF_A4_WIDTH
    );

    const rawWidth = this.toPreviewX(footerPlacement.width);
    const rawHeight = this.toPreviewY(footerPlacement.height);

    const maxFooterHeight = 18;
    const heightScale = rawHeight > maxFooterHeight ? maxFooterHeight / rawHeight : 1;

    const footerPreviewScale = 0.8;

    const finalWidth = rawWidth * heightScale * footerPreviewScale;
    const finalHeight = rawHeight * heightScale * footerPreviewScale;

    const bottomMargin = +(this.officeTemplateFormGroup.get('marginBottom')?.value || 0);

    this.footerTextStyle = {
      left: '50%',
      transform: 'translateX(-50%)',
      bottom: '8px',
    };

    this.footerPreviewStyle = {
      left: '50%',
      transform: 'translateX(-50%)',
      width: `${finalWidth}px`,
      height: `${finalHeight}px`,
      bottom: `${this.toPreviewY(bottomMargin)}px`,
    };
  }

  private scaleToFitPageWidthWithOptionalMargin(originalWidth: number, originalHeight: number, pageWidth: number): { x: number; width: number; height: number } {
    const fullWidthScale = pageWidth / originalWidth;
    const fullWidthHeight = originalHeight * fullWidthScale;

    if (fullWidthHeight <= this.FOOTER_MAX_HEIGHT) {
      return {
        x: 0,
        width: pageWidth,
        height: fullWidthHeight,
      };
    }

    const availableWidth = pageWidth - this.FOOTER_HORIZONTAL_MARGIN * 2;
    const marginScale = availableWidth / originalWidth;
    const marginHeight = originalHeight * marginScale;

    if (marginHeight <= this.FOOTER_MAX_HEIGHT) {
      return {
        x: this.FOOTER_HORIZONTAL_MARGIN,
        width: availableWidth,
        height: marginHeight,
      };
    }

    const heightScale = this.FOOTER_MAX_HEIGHT / originalHeight;
    const scaledWidth = originalWidth * heightScale;
    const x = (pageWidth - scaledWidth) / 2;

    return {
      x,
      width: scaledWidth,
      height: this.FOOTER_MAX_HEIGHT,
    };
  }

  private getImageSize(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
        URL.revokeObjectURL(url);
      };

      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not read image dimensions.'));
      };

      image.src = url;
    });
  }

  public async onHeaderLogoSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (!file) {
      return;
    }

    const error = await this.validateImageFile(file);

    if (error) {
      this.officeTemplateFormGroup.patchValue({ headerLogo: null });
      input.value = '';
      // show error message here
      return;
    }

    this.officeTemplateFormGroup.patchValue({ headerLogo: file });

    if (this.headerPreviewUrl) {
      URL.revokeObjectURL(this.headerPreviewUrl);
    }

    this.headerPreviewUrl = URL.createObjectURL(file);
    await this.buildHeaderPreviewStyle(file);
  }

  public async onFooterLogoSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    this.officeTemplateFormGroup.patchValue({ footerLogo: file });

    if (this.footerPreviewUrl) {
      URL.revokeObjectURL(this.footerPreviewUrl);
    }

    this.footerPreviewUrl = file ? URL.createObjectURL(file) : null;
    this.footerPreviewStyle = null;

    if (file) {
      await this.buildFooterPreviewStyle(file);
    }
  }

  private async validateImageFile(file: File): Promise<string | null> {
    if (!file.type.startsWith('image/')) {
      return 'Upload een geldig afbeeldingsbestand.';
    }

    if (file.size > this.MAX_IMAGE_BYTES) {
      return 'De afbeelding mag maximaal 2 MB zijn.';
    }

    const size = await this.getImageSize(file);

    if (size.width > this.MAX_IMAGE_WIDTH) {
      return `De afbeelding is te breed. Maximum is ${this.MAX_IMAGE_WIDTH}px.`;
    }

    if (size.height > this.MAX_IMAGE_HEIGHT) {
      return `De afbeelding is te hoog. Maximum is ${this.MAX_IMAGE_HEIGHT}px.`;
    }

    if (size.width * size.height > this.MAX_TOTAL_PIXELS) {
      return 'De afbeelding is te groot om te verwerken.';
    }

    return null;
  }
}
